import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import ToothGraphic5 from '@/assets/images/Tooth-graphic-5.svg'
import { isNullorEmpty } from '@/utils'
import { getFrameworkAdviceList } from '@/utils/matisseV2/allInOne'
import {
  fromFrameworkRecommendation,
  getFrameworkBrand,
} from '@/utils/matisseV2/frameworks'

type FrameworkPDFProps = Readonly<{
  recipe: any
  frameworkIndex: number
}>

const shouldSkipFramework = (indication?: string) =>
  indication === 'Veneer on refractory' || indication === 'PFM Crown'

function FrameworkBadges({
  advice,
  frameworkBrand,
}: {
  advice: any
  frameworkBrand?: string
}) {
  const name = advice?.name ?? advice?.substructurename
  return (
    <Box display="flex" alignItems="center" m={2}>
      <Typography
        variant="h5"
        component="div"
        sx={{ marginRight: 2, color: 'black' }}
      >
        Framework:
      </Typography>
      {frameworkBrand && (
        <Box
          sx={{
            backgroundColor: '#F44336',
            borderRadius: '16px',
            padding: '8px',
            color: 'black',
            marginLeft: 1,
          }}
        >
          {frameworkBrand}
        </Box>
      )}
      {name && (
        <Box
          sx={{
            backgroundColor: '#F44336',
            borderRadius: '16px',
            padding: '8px',
            color: 'black',
            marginLeft: 1,
          }}
        >
          {name}
        </Box>
      )}
    </Box>
  )
}

function formatThickness(value: unknown) {
  if (value == null || value === '') return '-'
  const n = Number(value)
  return Number.isFinite(n) ? `${n.toFixed(2)} mm` : '-'
}

function ThicknessRow({ advice }: { advice: any }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
        Thickness
      </TableCell>
      <TableCell sx={{ color: 'black' }}>
        {formatThickness(advice?.thickness_cervical)}
      </TableCell>
      <TableCell sx={{ color: 'black' }}>
        {formatThickness(advice?.thickness_middle)}
      </TableCell>
      <TableCell sx={{ color: 'black' }}>
        {formatThickness(advice?.thickness_incisal)}
      </TableCell>
    </TableRow>
  )
}

function StainingDeltaCell({
  advice,
  cervicalKey,
  middleKey,
  incisalKey,
}: {
  advice: any
  cervicalKey: string
  middleKey: string
  incisalKey: string
}) {
  return (
    <>
      <TableCell sx={{ color: 'black' }}>
        {isNullorEmpty(advice, cervicalKey, '1') ? (
          <>
            &Delta;A
            {advice?.[cervicalKey][1]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
        {isNullorEmpty(advice, cervicalKey, '2') ? (
          <>
            &Delta;B
            {advice?.[cervicalKey][2]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell sx={{ color: 'black' }}>
        {isNullorEmpty(advice, middleKey, '1') ? (
          <>
            &Delta;A
            {advice?.[middleKey][1]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
        {isNullorEmpty(advice, middleKey, '2') ? (
          <>
            &Delta;B
            {advice?.[middleKey][2]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell sx={{ color: 'black' }}>
        {isNullorEmpty(advice, incisalKey, '1') ? (
          <>
            &Delta;A
            {advice?.[incisalKey][1]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
        {isNullorEmpty(advice, incisalKey, '2') ? (
          <>
            &Delta;B
            {advice?.[incisalKey][2]?.toFixed(2)}
          </>
        ) : (
          '-'
        )}
      </TableCell>
    </>
  )
}

function DetailedStainingRows({ advice }: { advice: any }) {
  return (
    <>
      <TableRow>
        <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
          Estimated required staining
        </TableCell>
        <StainingDeltaCell
          advice={advice}
          cervicalKey="stainingdelLAB_cervical"
          middleKey="stainingdelLAB_middle"
          incisalKey="stainingdelLAB_incisal"
        />
      </TableRow>

      <TableRow>
        <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
          Staining brand
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.stain_cervical || '-'}
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.stain_middle || '-'}
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.stain_incisal || '-'}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
          Estimated required staining in VITA3D
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.VITAafterstaining_cervical || '-'}
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.VITAafterstaining_middle || '-'}
        </TableCell>
        <TableCell sx={{ color: 'black' }}>
          {advice?.VITAafterstaining_incisal || '-'}
        </TableCell>
      </TableRow>
    </>
  )
}

function NoStainingRow({ advice }: { advice: any }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
        Estimated required staining
      </TableCell>
      <TableCell colSpan={3} sx={{ color: 'black' }}>
        {advice?.instructions ||
          'There is no need to stain the framework, you can start with layering.'}
      </TableCell>
    </TableRow>
  )
}

function StainingSection({ advice, raw }: { advice: any; raw: any }) {
  const hasStainDelta = [
    'stainingdelLAB_cervical',
    'stainingdelLAB_middle',
    'stainingdelLAB_incisal',
  ].some((key) => raw?.[key] != null)
  if (advice?.staining && hasStainDelta) {
    return <DetailedStainingRows advice={raw} />
  }
  return <NoStainingRow advice={advice} />
}

function FrameworkPDF({ recipe, frameworkIndex }: FrameworkPDFProps) {
  const indication = recipe?.provided_input?.indication

  if (shouldSkipFramework(indication)) {
    return null
  }

  const rawAdvice = getFrameworkAdviceList(recipe)[frameworkIndex]
  const advice = fromFrameworkRecommendation(rawAdvice)
  const frameworkBrand = getFrameworkBrand(recipe)

  return (
    <Paper
      id="download-page-0"
      elevation={0}
      sx={{
        backgroundColor: '#FFF',
        px: 5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flexGrow: 1,
      }}
    >
      <Grid container sx={{ height: '100%' }}>
        <Grid item xs={4} sx={{ backgroundColor: 'white' }}>
          <Box
            component="img"
            src={ToothGraphic5}
            alt="Toothimage"
            sx={{ width: '233px', height: '330px' }}
          />
        </Grid>

        <Grid
          item
          xs={8}
          container
          direction="column"
          sx={{ backgroundColor: '#f8f9fa', flexGrow: 1 }}
        >
          <FrameworkBadges advice={advice} frameworkBrand={frameworkBrand} />

          <Box sx={{ px: 5, flexGrow: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: 'black' }} />
                  <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
                    Cervical
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
                    Middle
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'black' }}>
                    Incisal
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <ThicknessRow advice={advice} />
                <StainingSection advice={advice} raw={rawAdvice} />
              </TableBody>
            </Table>
          </Box>
        </Grid>
      </Grid>

      <Box
        component="hr"
        sx={{
          backgroundColor: 'black',
          opacity: 1,
          margin: 0,
          border: 0,
          height: '1px',
        }}
      />
    </Paper>
  )
}

export default FrameworkPDF
